import { Query } from '@/utils/react-query/Queries';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';

/**
 * Presentation model helpers for Auto-Schedule (spec §8.1 preview shape, §9.2 copy).
 *
 * CONTRACT-NOTE: the spec fixes the preview's top-level/task-level field names
 * but leaves the *identity* sub-shape ("authorized display identity") and the
 * exact warning/block reason-code vocabularies to the backend. All tolerant
 * field reads for those live ONLY in this file (toPerson, warningCodesOf,
 * blockCodesOf, reasonTextOf, readProgress, readReceipt) so a contract change
 * is a single-file fix.
 */

// ── Exact spec copy (§9.2) ──────────────────────────────────────────────────
export const COPY = {
    setupTitle: 'Auto-Schedule',
    horizonLabel: 'Schedule ahead',
    explanation: "Reach will use available team membership, staffing needs, attendance, recent assignments, blackout dates, and usual serving patterns across teams to suggest the best schedule it can for this period. Only people with recorded experience doing a task are suggested for it. You'll review each event and task, make any changes, and confirm before assignments are created. Suggestions may be incomplete when information is missing.",
    existingNote: 'Existing assignments will stay unchanged. Auto-Schedule only suggests people for open positions.',
    generateAction: 'Generate preview',
    cancelAction: 'Cancel',
    generatingHeading: 'Building your schedule',
    generatingDetail: 'Reviewing availability and serving patterns across your teams.',
    buildingDetail: 'Analyzing availability and serving history…',
    resumedDetail: 'A schedule was already in progress — picking it up where it left off.',
    cancelGeneration: 'Cancel generation',
    previewTitle: 'Review suggested schedule',
    nothingAssigned: 'Nothing has been assigned yet.',
    badgeExisting: 'Existing',
    badgeSuggested: 'Suggested',
    badgeEdited: 'Edited',
    back: 'Back',
    stale: 'This schedule changed while you were reviewing it. Nothing was assigned. Generate a new preview to review the latest availability.',
    expired: 'This preview expired. Generate a new preview before assigning volunteers.',
    modelFailure: "We couldn't build a valid schedule. Nothing was assigned. Try again or schedule volunteers manually.",
    noTasks: 'No upcoming volunteer tasks were found for these teams in this period.',
    noMembers: 'This team has no current members. Link a role with members before generating suggestions.',
    deliveryWarning: 'Assignments were created, but some notifications need another delivery attempt.',
    notificationsNote: 'Volunteers will receive normal notifications.',
    newPreview: 'Generate a new preview',
    canceled: 'Schedule generation canceled. Nothing was assigned.',
    warningsNeedAck: 'Some scheduling warnings need your acknowledgment — review the highlighted warnings, then confirm again.',
    draftRejected: 'A selection can no longer be scheduled — adjust the highlighted task and confirm again.',
    exclusionSet: 'will no longer be suggested for this task on this event in future runs.',
    exclusionRemoved: 'can be suggested for this task on this event again.',
};

export const summaryLine = (proposed, events) => `${proposed} suggested assignment${proposed === 1 ? '' : 's'} across ${events} event${events === 1 ? '' : 's'}`;
export const gapLine = (remaining) => `${remaining} position${remaining === 1 ? '' : 's'} still need${remaining === 1 ? 's' : ''} volunteers`;
export const progressLine = (completed, total) => `${completed} of ${total} event groups reviewed`;
export const confirmLabel = (count) => `Confirm ${count} assignment${count === 1 ? '' : 's'}`;
export const successLine = (count) => `${count} assignment${count === 1 ? '' : 's'} created.`;

// ── Allowlisted reason / warning / block copy ───────────────────────────────
// reasonCodes come from the model output contract (§6.1) re-expressed by the
// server per proposed person.
const REASON_COPY = {
    attendance_fit: 'Attendance history fits this event',
    usual_rotation: 'Fits their usual serving pattern',
    recent_task_experience: 'Recent experience with this task',
    balanced_workload: 'Balances workload across teams',
    preserves_other_commitment: 'Keeps their usual other commitment open',
    limited_history: 'Limited serving history — treated as eligible',
};
export const reasonCopy = (code) => REASON_COPY[code] || null;

// Soft-warning codes that may appear on candidates/proposals. Selecting a
// person carrying one requires a per-target/person/code acknowledgment.
const WARNING_COPY = {
    attendance_weekday_mismatch: 'Attendance history points to a different weekday',
    known_weekday_mismatch: 'Attendance history points to a different weekday',
    usual_rotation_mismatch: 'Outside their usual serving pattern',
    usual_other_commitment: 'Usually serves another team at this time',
    backup_commitment: 'Also committed as a backup at this time',
    limited_history: 'Limited serving history',
    no_prior_task_experience: 'No recorded experience doing this task',
    attendance_unknown: 'No recent attendance records; availability is uncertain.',
    attendance_sparse: 'No recent attendance records; availability is uncertain.',
    sparse_attendance: 'No recent attendance records; availability is uncertain.',
    unknown_attendance: 'No recent attendance records; availability is uncertain.',
    no_attendance_data: 'No recent attendance records; availability is uncertain.',
    missing_attendance: 'No recent attendance records; availability is uncertain.',
    // Acknowledgeable scheduling conflicts (requiredWarnings vocabulary —
    // _auto_schedule_check_draft). These used to be hard errors; the user may
    // now acknowledge and override them.
    cross_target_conflict: 'Scheduling conflict — this volunteer is assigned to two roles at the same time',
    actual_commitment_conflict: 'Scheduling conflict — already committed at this time',
    blackout_overlap: 'Overlaps a blackout period during this event',
    do_not_suggest: 'Marked "never suggest" for this task on this event',
};
export const warningCopy = (code) => WARNING_COPY[code] || 'Review before assigning';

/**
 * Evidence hard-block codes that validate/confirm treat as ACKNOWLEDGEABLE
 * warnings instead of hard errors (_auto_schedule_check_draft §requiredWarnings).
 * Maps the evidence code → the requiredWarnings code the server emits.
 * A candidate carrying ONLY these codes stays selectable; selecting them
 * produces an acknowledgment chip. Any other hard-block code still disables.
 */
export const ACKNOWLEDGEABLE_BLOCK_CODES = {
    actual_commitment: 'actual_commitment_conflict',
    do_not_suggest: 'do_not_suggest',
    blackout_overlap: 'blackout_overlap',
};

// Hard-blocked candidates are disabled with a safe reason (spec §9.2). Codes
// beyond this list fall back to the generic unavailable label — never expose
// underlying commitment details.
const BLOCK_COPY = {
    blackout: 'Unavailable during this event',
    blackout_overlap: 'Unavailable during this event',
    unavailable: 'Unavailable during this event',
    actual_conflict: 'Already scheduled',
    actual_commitment: 'Already scheduled',
    conflicting_commitment: 'Already scheduled',
    declined_occurrence: 'Declined this occurrence',
    declined: 'Declined this occurrence',
    not_team_member: 'No longer a team member',
    not_member: 'No longer a team member',
};
export const blockCopy = (code) => BLOCK_COPY[code] || 'Unavailable during this event';

// Server-side unfilled reasons (§6.1 output contract).
const UNFILLED_COPY = {
    no_eligible_members: 'No eligible members for this task',
    availability_constraints: 'No one left who is free at this time',
    competing_demand: 'Eligible members are needed elsewhere in this period',
    insufficient_information: 'Not enough information to suggest safely',
    manual_review_required: 'Needs manual review',
};
export const unfilledCopy = (code) => UNFILLED_COPY[code] || null;

// dataQualityWarnings are codes; keep display generic and actionable.
const DATA_QUALITY_COPY = {
    unknown_duration: 'Event end time is missing — this task could not be planned.',
    missing_timing: 'This task is missing timing and could not be planned.',
    materialization_gap: 'Some event occurrences are missing — generate the schedule for this series first.',
    possible_materialization_gap: 'Some event occurrences are missing — generate the schedule for this series first.',
    priority_review_required: 'Existing assignment order needs review before suggestions apply.',
    invalid_staffing: 'Required staffing is misconfigured for this task.',
    no_members: COPY.noMembers,
    no_linked_role: COPY.noMembers,
    missing_role: COPY.noMembers,
    empty_team: COPY.noMembers,
    attendance_unknown: 'No recent attendance records; availability is uncertain.',
};
export const dataQualityCopy = (code) => DATA_QUALITY_COPY[code] || null;

const RESPONSE_COPY = {
    ACCEPTED: 'Accepted',
    NO_RESPONSE: 'No response',
    DECLINED: 'Declined',
    PENDING: 'Pending',
};
export const responseCopy = (response) => RESPONSE_COPY[response] || response || '';

// ── Identity normalization (isolated contract assumption) ───────────────────
/**
 * Map a server identity object to the person-shaped object SheetSelector and
 * Avatar consume. Accepts {personID|id, firstName, lastName, displayName|name,
 * avatar*} and tolerates a nested { Person: {...} } / { person: {...} }.
 */
export const toPerson = (identity) => {
    if (!identity) return null;
    const src = identity.Person || identity.person || identity;
    const id = src.personID || src.id;
    if (!id) return null;
    let firstName = src.firstName || '';
    let lastName = src.lastName || '';
    const display = src.displayName || src.name || '';
    if (!firstName && !lastName && display) {
        const idx = display.lastIndexOf(' ');
        if (idx > 0) { firstName = display.slice(0, idx); lastName = display.slice(idx + 1); }
        else firstName = display;
    }
    return {
        ...src,
        id,
        personID: id,
        firstName,
        lastName,
        displayName: display || `${firstName} ${lastName}`.trim(),
    };
};
export const personLabel = (person) => person?.displayName || `${person?.firstName || ''} ${person?.lastName || ''}`.trim() || 'Volunteer';

// ── Tolerant field reads (isolated contract assumptions) ────────────────────
export const warningCodesOf = (entry) => {
    // autoExclusionCodes are soft-warning codes that keep a candidate out of
    // AI proposals but still require manual-override acknowledgment — the
    // server validates acks against their union (requiredWarnings).
    const warnings = entry?.warningCodes ?? entry?.warnings ?? [];
    const exclusions = entry?.autoExclusionCodes ?? [];
    // Evidence-side acknowledgeable blocks surface as warnings here too —
    // the server maps them into requiredWarnings under the codes in
    // ACKNOWLEDGEABLE_BLOCK_CODES, so the client mirror must match.
    const acknowledgeable = blockCodesOf(entry)
        .map(c => ACKNOWLEDGEABLE_BLOCK_CODES[c])
        .filter(Boolean);
    const codes = [
        ...(Array.isArray(warnings) ? warnings : []),
        ...(Array.isArray(exclusions) ? exclusions : []),
        ...acknowledgeable,
    ];
    return [...new Set(codes
        .map(w => (typeof w === 'string' ? w : w?.code))
        .filter(Boolean))];
};
export const blockCodesOf = (candidate) => {
    const codes = candidate?.hardBlockCodes ?? candidate?.blockReasonCodes ?? candidate?.blockedReasons ?? candidate?.blockCodes ?? [];
    return (Array.isArray(codes) ? codes : []).map(c => (typeof c === 'string' ? c : c?.code)).filter(Boolean);
};
/** Block codes that are truly non-acknowledgeable (still disable the option). */
export const nonAcknowledgeableBlockCodesOf = (candidate) =>
    blockCodesOf(candidate).filter(code => !ACKNOWLEDGEABLE_BLOCK_CODES[code]);
export const isHardBlocked = (candidate) => {
    const hard = nonAcknowledgeableBlockCodesOf(candidate);
    if (hard.length > 0) return true;
    // Codes present but all acknowledgeable → selectable. An unexplained
    // `hardBlocked: true` flag stays blocking (safe default).
    return candidate?.hardBlocked === true && blockCodesOf(candidate).length === 0;
};
export const reasonTextOf = (proposal) => {
    const summary = proposal?.evidenceSummary;
    if (typeof summary === 'string' && summary) return summary;
    if (Array.isArray(summary)) {
        const joined = summary.map(s => (typeof s === 'string' ? s : s?.label || s?.text)).filter(Boolean).join('; ');
        if (joined) return joined;
    }
    const codes = Array.isArray(proposal?.reasonCodes) ? proposal.reasonCodes : [];
    const mapped = codes.map(reasonCopy).filter(Boolean);
    return mapped.join('; ') || null;
};

/** Server progress object → {completed, total} ("event groups reviewed"). */
export const readProgress = (data) => {
    const p = data?.progress;
    if (!p) return null;
    const completed = p.batchesCompleted ?? p.completed ?? p.completedGroups ?? p.done;
    const total = p.batchesTotal ?? p.total ?? p.totalGroups ?? p.count;
    if (!Number.isFinite(completed) || !Number.isFinite(total)) return null;
    return { completed, total };
};

/** Confirm receipt → {count, deliveryWarning}. */
export const readReceipt = (data) => {
    const receipt = data?.receipt ?? data?.confirmationResult ?? data ?? {};
    const count = receipt.insertedCount ?? receipt.assignmentCount ?? receipt.assignments?.length ?? receipt.createdCount ?? null;
    const notificationState = receipt.notificationState ?? receipt.notifications ?? null;
    const deliveryWarning = receipt.deliveryWarning === true
        || receipt.notificationWarning === true
        || (typeof notificationState === 'string' && !['delivered', 'sent', 'none', 'disabled'].includes(notificationState))
        || (Array.isArray(notificationState) && notificationState.some(n => n?.status && n.status !== 'delivered'));
    return { count, deliveryWarning: !!deliveryWarning };
};

/** Preview task rows that cannot be planned at all (missing timing etc.). */
export const taskUnplannableReason = (task) => {
    if (task?.unplannableReason) return typeof task.unplannableReason === 'string' ? task.unplannableReason : null;
    const warnings = Array.isArray(task?.dataQualityWarnings) ? task.dataQualityWarnings : [];
    for (const w of warnings) {
        const code = typeof w === 'string' ? w : w?.code;
        const copy = dataQualityCopy(code) || (typeof w === 'object' ? w?.message : null);
        if (copy) return copy;
    }
    return task?.unplannable === true ? 'This task could not be planned.' : null;
};

// ── Draft model ─────────────────────────────────────────────────────────────

/**
 * Build the initial draft from a ready preview: every target maps to its
 * proposed additions (personIDs only — existing assignees are locked and
 * never part of the draft).
 * @returns {{draft: Object<string,string[]>, peopleByID: Map, targets: Array}}
 */
export const buildDraftState = (preview) => {
    const draft = {};
    const peopleByID = new Map();
    const targets = [];

    (preview?.events || []).forEach((event) => {
        (event?.teams || []).forEach((team) => {
            (team?.tasks || []).forEach((task) => {
                if (!task?.taskInstanceID) return;
                const candidates = (task.candidateOptions || []).map((c) => ({
                    person: toPerson(c),
                    hardBlocked: isHardBlocked(c),
                    // Only non-acknowledgeable codes stay in blockCodes (they
                    // drive the disabled-row reason); acknowledgeable codes
                    // are already folded into warningCodes.
                    blockCodes: nonAcknowledgeableBlockCodesOf(c),
                    warningCodes: warningCodesOf(c),
                })).filter(c => c.person?.id);
                const proposed = (task.proposedAssignees || []).map(p => ({
                    person: toPerson(p),
                    warningCodes: warningCodesOf(p),
                    reasonText: reasonTextOf(p),
                })).filter(p => p.person?.id);
                const existing = (task.existingAssignees || []).map(e => ({
                    person: toPerson(e),
                    response: e?.response,
                    position: e?.position,
                    locked: e?.locked !== false,
                })).filter(e => e.person?.id);

                candidates.forEach(c => peopleByID.set(c.person.id, c.person));
                proposed.forEach(p => peopleByID.set(p.person.id, p.person));
                existing.forEach(e => peopleByID.set(e.person.id, e.person));

                draft[task.taskInstanceID] = proposed.map(p => p.person.id);
                targets.push({
                    taskInstanceID: task.taskInstanceID,
                    taskID: task.taskID,
                    title: task.title || 'Task',
                    locationID: task.locationID ?? null,
                    needed: task.needed ?? null,
                    openCount: Number.isFinite(task.openCount) ? task.openCount : null,
                    remainingCount: task.remainingCount ?? null,
                    unfilledReasonCode: task.unfilledReasonCode ?? null,
                    dataQualityWarnings: Array.isArray(task.dataQualityWarnings) ? task.dataQualityWarnings : [],
                    existing,
                    proposed,
                    candidates,
                    teamID: team?.teamID ?? null,
                    teamName: team?.name || 'Team',
                    eventInstanceID: event?.eventInstanceID,
                    eventID: event?.eventID ?? null,
                    eventName: event?.displayName || 'Event',
                    locationLabel: event?.locationLabel || null,
                    startTime: event?.startTime ?? null,
                    endTime: event?.endTime ?? null,
                });
            });
        });
    });

    return { draft, peopleByID, targets };
};

/** Canonical per-person warning codes for a target (candidate or proposal). */
export const warningCodesFor = (target, personID) => {
    const fromCandidate = target.candidates.find(c => c.person.id === personID)?.warningCodes || [];
    const fromProposal = target.proposed.find(p => p.person.id === personID)?.warningCodes || [];
    return [...new Set([...fromCandidate, ...fromProposal])];
};

export const ackKey = (taskInstanceID, personID, code) => `${taskInstanceID}|${personID}|${code}`;

/**
 * Every (taskInstanceID, personID, code) pair requiring an acknowledgment
 * chip. Mirrors _auto_schedule_check_draft's requiredWarnings:
 *  - candidate/proposal soft codes (warnings + autoExclusions + mapped
 *    acknowledgeable blocks like do_not_suggest/blackout_overlap),
 *  - 'do_not_suggest' for people excluded via a locally-known
 *    AutoScheduleExclusions row (`isExcluded(target, personID)`),
 *  - 'cross_target_conflict' for draft-internal double-booking, emitted on
 *    BOTH colliding targets exactly like the server.
 */
export const requiredAcknowledgments = (targets, draft, isExcluded = () => false) => {
    const required = [];
    const seen = new Set();
    const push = (taskInstanceID, personID, code) => {
        const key = ackKey(taskInstanceID, personID, code);
        if (seen.has(key)) return;
        seen.add(key);
        required.push({ taskInstanceID, personID, code });
    };
    targets.forEach(target => {
        (draft[target.taskInstanceID] || []).forEach(personID => {
            const codes = warningCodesFor(target, personID);
            if (isExcluded(target, personID) && !codes.includes('do_not_suggest')) codes.push('do_not_suggest');
            codes.forEach(code => push(target.taskInstanceID, personID, code));
        });
    });
    computeDraftConflicts(targets, draft).forEach(c => push(c.taskInstanceID, c.personID, 'cross_target_conflict'));
    return required;
};

export const missingAcknowledgments = (targets, draft, acks, isExcluded) =>
    requiredAcknowledgments(targets, draft, isExcluded).filter(r => !acks.has(ackKey(r.taskInstanceID, r.personID, r.code)));

/**
 * Draft-vs-draft conflicts: a person selected for two targets that overlap.
 * Same event occurrence is conservatively a conflict (spec §4.3); across
 * occurrences, overlap is computed from the event [startTime, endTime)
 * intervals the preview supplies. Targets with unknown intervals only
 * conflict within the same occurrence — task-level time/dueDate extension is
 * not part of the preview contract and remains a backend concern.
 * @returns Map<`${taskInstanceID}|${personID}`, {taskInstanceID, personID, otherTaskInstanceID, otherTitle, otherEventName}>
 */
export const computeDraftConflicts = (targets, draft) => {
    const byPerson = new Map();
    targets.forEach(target => {
        (draft[target.taskInstanceID] || []).forEach(personID => {
            if (!byPerson.has(personID)) byPerson.set(personID, []);
            byPerson.get(personID).push(target);
        });
    });

    const overlaps = (a, b) => {
        if (a.eventInstanceID === b.eventInstanceID) return true;
        if (!a.startTime || !a.endTime || !b.startTime || !b.endTime) return false;
        const aStart = new Date(a.startTime).getTime();
        const aEnd = new Date(a.endTime).getTime();
        const bStart = new Date(b.startTime).getTime();
        const bEnd = new Date(b.endTime).getTime();
        if (![aStart, aEnd, bStart, bEnd].every(Number.isFinite)) return false;
        return aStart < bEnd && bStart < aEnd;
    };

    const conflicts = new Map();
    byPerson.forEach((list, personID) => {
        for (let i = 0; i < list.length; i++) {
            for (let j = i + 1; j < list.length; j++) {
                const a = list[i];
                const b = list[j];
                if (!overlaps(a, b)) continue;
                conflicts.set(`${a.taskInstanceID}|${personID}`, { taskInstanceID: a.taskInstanceID, personID, otherTaskInstanceID: b.taskInstanceID, otherTitle: b.title, otherEventName: b.eventName });
                conflicts.set(`${b.taskInstanceID}|${personID}`, { taskInstanceID: b.taskInstanceID, personID, otherTaskInstanceID: a.taskInstanceID, otherTitle: a.title, otherEventName: a.eventName });
            }
        }
    });
    return conflicts;
};

/** Stable draft identity used to bind the validation token to this exact draft. */
export const draftHash = (targets, draft) => {
    const rows = targets
        .map(t => ({ taskInstanceID: t.taskInstanceID, personIDs: [...(draft[t.taskInstanceID] || [])].sort() }))
        .sort((a, b) => (a.taskInstanceID < b.taskInstanceID ? -1 : 1));
    return JSON.stringify(rows);
};

/** Validate payload: one row for EVERY target, including empty selections. */
export const validatePayload = (targets, draft) =>
    targets.map(t => ({ taskInstanceID: t.taskInstanceID, personIDs: [...(draft[t.taskInstanceID] || [])] }));

/**
 * Serialize the ack set for validate. `required` scopes the payload to the
 * currently-required (taskInstanceID, personID, code) triples — stale acks
 * (e.g. a do_not_suggest ack surviving after the exclusion was removed, or a
 * cross_target ack after one side was emptied) would otherwise trip the
 * server's `acknowledgment_not_applicable` exact-set check.
 */
export const acknowledgedWarningsPayload = (acks, required = null) => {
    const allowed = required ? new Set(required.map(r => ackKey(r.taskInstanceID, r.personID, r.code))) : null;
    return [...acks]
        .filter(key => !allowed || allowed.has(key))
        .map(key => {
            const [taskInstanceID, personID, ...rest] = key.split('|');
            return { taskInstanceID, personID, code: rest.join('|') };
        });
};

/** Total proposed additions currently in the draft. */
export const draftAdditionCount = (draft) => Object.values(draft || {}).reduce((sum, ids) => sum + (ids?.length || 0), 0);

// ── Access gate (spec §1.3) ─────────────────────────────────────────────────
/**
 * Shared client gate for both Auto-Schedule buttons and the modal itself.
 * Auto-Schedule reuses the existing `Use AI Assistant` permission plus the
 * paid `assistant` module entitlement — no new permission is invented and the
 * backend remains authoritative.
 * @returns {{allowed: boolean, reason: string|null, isAdmin: boolean, loggedInUser: object|null}}
 */
export const useAutoScheduleAccess = () => {
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();
    const hasModule = canViewModule(entitlements, 'assistant');
    const hasPermission = !!loggedInUser?.can?.('Use AI Assistant');
    const reason = isFreeOrg(entitlements)
        ? 'Auto-Schedule is part of the paid plan.'
        : !hasModule
            ? 'Auto-Schedule is not available on this plan.'
            : !hasPermission
                ? 'Auto-Schedule needs the “Use AI Assistant” permission. Ask your Reach administrator to grant it.'
                : null;
    return { allowed: hasModule && hasPermission, reason, isAdmin: !!loggedInUser?.isAdmin, loggedInUser };
};

/** Event-level structure for rendering (events -> teams -> targets). */
export const groupTargetsByEvent = (targets) => {
    const events = new Map();
    targets.forEach(target => {
        const key = target.eventInstanceID || 'unknown';
        if (!events.has(key)) {
            events.set(key, { eventInstanceID: key, eventName: target.eventName, locationLabel: target.locationLabel, startTime: target.startTime, endTime: target.endTime, teams: new Map() });
        }
        const event = events.get(key);
        const teamKey = target.teamID || target.teamName;
        if (!event.teams.has(teamKey)) event.teams.set(teamKey, { teamID: target.teamID, teamName: target.teamName, targets: [] });
        event.teams.get(teamKey).targets.push(target);
    });
    return [...events.values()].map(e => ({ ...e, teams: [...e.teams.values()] }));
};

/** Same task/team occupies one row, even across different event series. */
export const buildPreviewGrid = (targets) => {
    const events = groupTargetsByEvent(targets).sort((a, b) =>
        (Date.parse(a.startTime) || 0) - (Date.parse(b.startTime) || 0)
        || a.eventInstanceID.localeCompare(b.eventInstanceID));
    const rows = new Map();
    targets.forEach(target => {
        const key = `${target.teamID || target.teamName}|${target.taskID || target.taskInstanceID}`;
        if (!rows.has(key)) rows.set(key, { key, teamName: target.teamName, title: target.title, byEvent: new Map() });
        const byEvent = rows.get(key).byEvent;
        const eventKey = target.eventInstanceID || 'unknown';
        if (!byEvent.has(eventKey)) byEvent.set(eventKey, []);
        byEvent.get(eventKey).push(target);
    });
    return {
        events,
        rows: [...rows.values()].sort((a, b) => a.teamName.localeCompare(b.teamName) || a.title.localeCompare(b.title) || a.key.localeCompare(b.key)),
    };
};
